import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useUpdateBoardNode } from 'hooks/Board';
import { convertReferenceNodesToText } from '../utils/referenceUtils';
import {
  UseBoardNodeTipTapEditorProps,
  hasBlocks,
  FocusHandlerFn,
} from '../types/editorTypes';
import { convertReferencesToTemplateFormat } from '../utils/referenceTransformUtils';

/**
 * Hook for board node specific TipTap functionality
 * Handles content editing, saving, and focus management
 */
export const useBoardNodeTipTapEditor = ({
  boardNodeId,
  boardId,
}: UseBoardNodeTipTapEditorProps) => {
  const { mutateAsync: updateBoardNode } = useUpdateBoardNode(
    boardNodeId,
    boardId
  );

  // State for tracking editing status
  const [latestContent, setLatestContent] = useState<string>('');

  // Use refs for values that don't need to trigger re-renders
  const saveInProgress = useRef(false);
  const editorFocused = useRef(false);
  const pendingSave = useRef(false);
  const pendingChanges = useRef(false);

  // Check if content is empty
  const isContentEmpty = useCallback((content: any): boolean => {
    if (!content) return true;
    if (content === '') return true;
    if (content === '<p></p>') return true;
    if (hasBlocks(content) && (!content.blocks || content.blocks.length === 0))
      return true;
    return false;
  }, []);

  // Function to validate content before saving
  const isValidContent = useCallback((content: string): boolean => {
    try {
      if (!content) return true; // Empty content is valid
      // Basic check for valid JSON structure when wrapped
      JSON.stringify({ content });
      return true;
    } catch (error) {
      console.error('Invalid content structure:', error);
      return false;
    }
  }, []);

  // References to store the parameters for editor focus handling
  const nodeRefForFocus = useRef<HTMLElement | null>(null);
  const containerIdForFocus = useRef<string>('');
  const getLastFocusedElementRef = useRef<FocusHandlerFn | null>(null);

  // Setup editor focus handler with the provided parameters
  const setupEditorFocusHandler = useCallback(
    (
      nodeRef: React.RefObject<HTMLElement>,
      boardNodeContentContainerID: string,
      getLastFocusedElement?: FocusHandlerFn
    ) => {
      // Store a reference to the node element (if it exists)
      nodeRefForFocus.current = nodeRef.current;
      containerIdForFocus.current = boardNodeContentContainerID;
      getLastFocusedElementRef.current = getLastFocusedElement || null;

      // If the nodeRef is not yet available but the ref object exists,
      // set up a MutationObserver to detect when it becomes available
      if (!nodeRef.current && nodeRef) {
        // Check again on next frame in case the DOM is still updating
        requestAnimationFrame(() => {
          if (nodeRef.current) {
            nodeRefForFocus.current = nodeRef.current;
          }
        });
      }
    },
    []
  );

  // Effect to handle TipTap editor focus events
  useEffect(() => {
    // Safely handle cases where the node reference might not be available
    if (!nodeRefForFocus.current) {
      return; // Exit early if no node reference
    }

    if (!getLastFocusedElementRef.current || !containerIdForFocus.current) {
      return; // Exit early if other required references are missing
    }

    // Add listener for custom tiptap focus event
    const handleTiptapFocus = (event: CustomEvent) => {
      try {
        if (
          event.detail &&
          event.detail.editorElement &&
          getLastFocusedElementRef.current
        ) {
          getLastFocusedElementRef.current(
            {
              target: event.detail.editorElement,
            } as unknown as React.MouseEvent<HTMLElement, MouseEvent>,
            containerIdForFocus.current
          );
        }
      } catch (error) {
        console.error('Error handling TipTap focus event:', error);
      }
    };

    // Add the event listener to the node container
    const nodeContainer = nodeRefForFocus.current;
    if (nodeContainer) {
      nodeContainer.addEventListener(
        'tiptap-editor-focused',
        handleTiptapFocus as EventListener
      );

      // Cleanup
      return () => {
        nodeContainer.removeEventListener(
          'tiptap-editor-focused',
          handleTiptapFocus as EventListener
        );
      };
    }

    return undefined; // Return empty cleanup function if no nodeContainer
  }, []); // Depend on the reference value to re-attach if it changes

  // Format content for database storage - transform reference spans to mustache braces
  const formatContentForDatabase = useCallback((html: string): string => {
    if (!html) return html;

    const formattedWithUtility = convertReferenceNodesToText(html);

    // If the utility successfully made changes, use that result
    if (formattedWithUtility !== html) {
      return formattedWithUtility;
    }

    // Fallback: Handle different possible formats of reference spans with direct regex
    let formattedContent = html;

    // 1. Handle the exact format seen in the payload: <span data-reference="" id="inputs.xyz"></span>
    formattedContent = formattedContent.replace(
      /<span\s+data-reference=\"\"\s+id=\"(inputs|steps)\.([\w-]+)\"[^>]*><\/span>/g,
      (match: string, type: string, id: string) => {
        return `{{${type}.${id}}}`;
      }
    );

    // 2. Try alternate attribute order: id first, then data-reference
    if (formattedContent === html) {
      formattedContent = formattedContent.replace(
        /<span\s+id=\"(inputs|steps)\.([\w-]+)\"\s+data-reference=\"\"[^>]*><\/span>/g,
        (match: string, type: string, id: string) => {
          return `{{${type}.${id}}}`;
        }
      );
    }

    // 3. More generic pattern for data-reference span with id somewhere
    if (formattedContent === html) {
      formattedContent = formattedContent.replace(
        /<span\s+[^>]*(?:data-reference[^>]*id=\"(inputs|steps)\.([\w-]+)\"|id=\"(inputs|steps)\.([\w-]+)\"[^>]*data-reference)[^>]*><\/span>/g,
        (
          match: string,
          type1: string,
          id1: string,
          type2: string,
          id2: string
        ) => {
          const type = type1 || type2;
          const id = id1 || id2;
          return `{{${type}.${id}}}`;
        }
      );
    }

    // 4. Handle data-reference-id format (original)
    if (formattedContent === html) {
      formattedContent = formattedContent.replace(
        /<span data-reference-id="(inputs|steps)\.([^"]+)"[^>]*><\/span>/g,
        (match: string, type: string, id: string) => {
          return `{{${type}.${id}}}`;
        }
      );
    }

    // 5. Alternative format with other attributes (original)
    if (formattedContent === html) {
      formattedContent = formattedContent.replace(
        /<span [^>]*data-reference-id="(inputs|steps)\.([^"]+)"[^>]*><\/span>/g,
        (match: string, type: string, id: string) => {
          return `{{${type}.${id}}}`;
        }
      );
    }

    // Remove any whitespace between adjacent references that might have been introduced
    formattedContent = formattedContent.replace(/}}(\s*){{/g, () => '}}{{');

    return formattedContent;
  }, []);

  // Save content to backend with proper handling of references
  const saveContentWithData = useCallback(
    async (contentData: any) => {
      // If already saving, just mark as pending and return
      if (saveInProgress.current) {
        pendingSave.current = true;
        return;
      }

      saveInProgress.current = true;

      try {
        // Use the passed content directly
        const output = contentData;

        // Double check if output is empty or just a blank paragraph - don't save in that case
        if (isContentEmpty(output)) {
          saveInProgress.current = false;
          pendingChanges.current = false;
          return;
        }

        // For non-JSON content (like HTML strings), first convert to JSON structure
        let jsonData;
        try {
          // Try to parse as JSON first
          jsonData = JSON.parse(output);

          // Convert any reference nodes to template format before saving
          jsonData = convertReferencesToTemplateFormat(jsonData);
        } catch (e) {
          // Not valid JSON (likely HTML) - format HTML references and create basic doc structure
          const formattedContent = formatContentForDatabase(output);
          jsonData = {
            type: 'doc',
            content: [
              {
                type: 'paragraph',
                content: [
                  {
                    type: 'text',
                    text: formattedContent,
                  },
                ],
              },
            ],
          };
        }

        // Prepare the update payload - just data parameter with JSON content
        const updatedData = {
          data: JSON.stringify(jsonData),
        };

        // Reset pending changes flag as we're about to save
        pendingChanges.current = false;

        try {
          await updateBoardNode(updatedData);
          saveInProgress.current = false;

          // If a save was pending while this one was in progress, process it now
          if (pendingSave.current) {
            pendingSave.current = false;
            setTimeout(() => saveContent(), 100);
          }
        } catch (apiError: any) {
          saveInProgress.current = false;
          throw apiError;
        }
      } catch (error) {
        console.error(
          'useBoardNodeTipTapEditor: Error in saveContentWithData:',
          error
        );
        saveInProgress.current = false;
        throw error;
      }
    },
    [formatContentForDatabase, isContentEmpty, updateBoardNode]
  );

  // Save content using latest content state
  const saveContent = useCallback(async () => {
    saveContentWithData(latestContent);
  }, [latestContent, saveContentWithData]);

  // Function for handling content changes
  const handleChange = useCallback(
    async (output: any) => {
      // Check if output is empty or just a blank paragraph
      if (isContentEmpty(output)) {
        return;
      }

      // Store the latest content
      setLatestContent(output);

      // Set a flag to indicate unsaved changes
      if (!pendingChanges.current) {
        pendingChanges.current = true;
      }

      // Call a direct save function with the output parameter
      saveContentWithData(output).catch((error) => {
        console.error('useBoardNodeTipTapEditor: Error saving content:', error);
      });
    },
    [isContentEmpty, saveContentWithData]
  );

  // Auto-save logic
  useEffect(() => {
    const interval = setInterval(() => {
      if (
        pendingChanges.current &&
        !saveInProgress.current &&
        !editorFocused.current
      ) {
        saveContent();
      }
    }, 10000); // Auto-save every 10 seconds if changes pending

    return () => clearInterval(interval);
  }, [saveContent]);

  // Handler for editor blur events to save content
  const handleEditorBlur = useCallback(() => {
    editorFocused.current = false;

    // Only save if there are pending changes
    if (pendingChanges.current && !saveInProgress.current) {
      saveContent();
    }
  }, [saveContent]);

  // Handler for editor focus events
  const handleEditorFocus = useCallback(() => {
    editorFocused.current = true;
  }, []);

  return {
    handleChange,
    handleEditorBlur,
    handleEditorFocus,
    setupEditorFocusHandler,
    saveContent,
    convertReferencesToTemplateFormat,
    formatContentForDatabase,
    saveContentWithData,
    isValidContent,
    isContentEmpty,
  };
};
